import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";

import { NotificationCenterBellTrigger } from "./components/NotificationCenterBellTrigger";
import { NotificationCenterCount } from "./components/NotificationCenterCount";
import { NotificationCenterEmptyState } from "./components/NotificationCenterEmptyState";
import { NotificationCenterError } from "./components/NotificationCenterError";
import { NotificationCenterFooter } from "./components/NotificationCenterFooter";
import { NotificationCenterHeader } from "./components/NotificationCenterHeader";
import { NotificationCenterItem } from "./components/NotificationCenterItem";
import { NotificationCenterList } from "./components/NotificationCenterList";
import { NotificationCenterPanel } from "./components/NotificationCenterPanel";
import { NotificationCenterSkeleton } from "./components/NotificationCenterSkeleton";
import { NotificationCenterTrigger } from "./components/NotificationCenterTrigger";
import { NotificationCenterContext } from "./NotificationCenter.context";
import {
  type NotificationCenterContextValue,
  type NotificationCenterError as NotificationError,
  type NotificationCenterItem as NotificationItem,
} from "./NotificationCenter.types";

type Props<T> = {
  items: NotificationItem<T>[];
  onMarkRead?: (id: string) => void;
  onMarkAllRead?: () => void;
  onMarkUnread?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onItemClick?: (notification: NotificationItem<T>) => void;
  totalCount?: number;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
  isRetrying?: boolean;
  error?: NotificationError;
  onRetry?: () => void;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
};

function NotificationCenterRoot<T>({
  items,
  onMarkRead,
  onMarkAllRead,
  onMarkUnread,
  onDismiss,
  onItemClick,
  totalCount,
  hasMore,
  isLoadingMore,
  onLoadMore,
  isLoading,
  isRetrying,
  error,
  onRetry,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
}: Props<T>) {
  const isControlledRef = useRef(controlledOpen !== undefined);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = isControlledRef.current
    ? (controlledOpen ?? false)
    : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlledRef.current) setUncontrolledOpen(next);

      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const unreadCount = items.filter((item) => !item.readAt).length;

  const value = useMemo<NotificationCenterContextValue>(
    () => ({
      items: items as NotificationItem[],
      unreadCount,
      isOpen,
      setOpen,
      markRead: (id) => onMarkRead?.(id),
      markAllRead: () => onMarkAllRead?.(),
      markUnread: onMarkUnread,
      dismiss: onDismiss,
      onItemClick: onItemClick as ((n: NotificationItem) => void) | undefined,
      totalCount,
      hasMore,
      isLoadingMore,
      onLoadMore,
      isLoading,
      isRetrying,
      error,
      onRetry,
    }),
    [
      items,
      unreadCount,
      isOpen,
      setOpen,
      onMarkRead,
      onMarkAllRead,
      onMarkUnread,
      onDismiss,
      onItemClick,
      totalCount,
      hasMore,
      isLoadingMore,
      onLoadMore,
      isLoading,
      isRetrying,
      error,
      onRetry,
    ],
  );

  return (
    <NotificationCenterContext.Provider value={value}>
      <DialogPrimitive.Root modal={false} onOpenChange={setOpen} open={isOpen}>
        {children ?? (
          <>
            <NotificationCenterBellTrigger />
            <NotificationCenterPanel />
          </>
        )}
      </DialogPrimitive.Root>
    </NotificationCenterContext.Provider>
  );
}

NotificationCenterRoot.Trigger = NotificationCenterTrigger;
NotificationCenterRoot.BellTrigger = NotificationCenterBellTrigger;
NotificationCenterRoot.Panel = NotificationCenterPanel;
NotificationCenterRoot.Header = NotificationCenterHeader;
NotificationCenterRoot.List = NotificationCenterList;
NotificationCenterRoot.Item = NotificationCenterItem;
NotificationCenterRoot.EmptyState = NotificationCenterEmptyState;
NotificationCenterRoot.ErrorState = NotificationCenterError;
NotificationCenterRoot.Skeleton = NotificationCenterSkeleton;
NotificationCenterRoot.Count = NotificationCenterCount;
NotificationCenterRoot.Footer = NotificationCenterFooter;

export const NotificationCenter = NotificationCenterRoot;
